import { TFunction } from 'i18next';

import { catchErrorProps } from 'hooks/useErrorCatch';

import { isBlobObjectUrl, isHttpUrl } from 'utils/urlUtils';

interface DownloadFileArgs {
  dataUrl: string;
  catchError: (props: catchErrorProps) => void;
  translation: TFunction<'common', undefined>;
  isOpenFile?: boolean;
  name?: string;
}

const DEFAULT_FILE_NAME = 'file';

const downloadFile = ({
  dataUrl,
  catchError,
  isOpenFile = false,
  translation,
  name = DEFAULT_FILE_NAME
}: DownloadFileArgs) => {
  try {
    const link = document.createElement('a');
    link.href = dataUrl;

    const openInNewTab = (): void => {
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
    };

    const isHttp = isHttpUrl(dataUrl);
    const isBlobUrl = isBlobObjectUrl(dataUrl);

    // Browsers may ignore `download` for cross-origin URLs and navigate away.
    // Use a new tab for external links to avoid leaving the current page.
    if (isHttp && !isBlobUrl) {
      try {
        const urlObj = new URL(dataUrl);
        if (urlObj.origin !== window.location.origin) {
          openInNewTab();
        }
      } catch {}
    }

    if (isOpenFile) {
      openInNewTab();
    } else {
      link.setAttribute('download', name);
    }

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    catchError({
      error,
      default: {
        message: translation('error.message.operationFailed'),
        description: translation('error.message.downloadFile')
      },
      consoleMessage: JSON.stringify(error)
    });
  }
};

export default downloadFile;
